import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  BarChart3,
  PieChart,
  TrendingUp,
  Calendar,
  Tag,
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  BookOpen,
  Star,
  Activity,
  Layers
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PlanStudioAnalytics = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, analyticsRes] = await Promise.all([
        fetch(`${API}/api/plan-studio/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/api/plan-studio/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check access
  if (!user?.email?.endsWith('@yomev.org')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Erişim Engellendi</h2>
          <p className="text-gray-600 mt-2">Bu sayfa sadece YÖMEV kullanıcılarına açıktır.</p>
        </div>
      </div>
    );
  }

  const planTypeLabels = {
    'CORE': 'Temel',
    'TURKCE': 'Türkçe',
    'MATEMATIK': 'Matematik'
  };

  const statusLabels = {
    'draft': 'Taslak',
    'in_review': 'İncelemede',
    'approved': 'Onaylı',
    'published': 'Yayında',
    'rejected': 'Reddedildi'
  };

  const statusColors = {
    'draft': 'bg-gray-100 text-gray-700',
    'in_review': 'bg-amber-100 text-amber-700',
    'approved': 'bg-green-100 text-green-700',
    'published': 'bg-blue-100 text-blue-700',
    'rejected': 'bg-red-100 text-red-700'
  };

  const getMaxValue = (obj) => Math.max(...Object.values(obj || {}), 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/plan-studio" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2">
              <ArrowLeft size={16} />
              Plan Studio'ya Dön
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              Analitik Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Plan Studio performans metrikleri ve istatistikler</p>
          </div>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            data-testid="refresh-analytics"
          >
            <RefreshCw size={18} />
            Yenile
          </button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalPlans || 0}</p>
                <p className="text-sm text-gray-500">Toplam Plan</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.publishedPlans || 0}</p>
                <p className="text-sm text-gray-500">Yayında</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalActivities || 0}</p>
                <p className="text-sm text-gray-500">Etkinlik</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics?.feedbackStats?.averageRating || '-'}
                </p>
                <p className="text-sm text-gray-500">Ort. Puan</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Plan Type Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <PieChart className="w-5 h-5 text-blue-600" />
              Plan Türü Dağılımı
            </h3>
            {analytics?.planTypeDistribution && Object.keys(analytics.planTypeDistribution).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(analytics.planTypeDistribution).map(([type, count]) => {
                  const maxVal = getMaxValue(analytics.planTypeDistribution);
                  const percentage = (count / maxVal) * 100;
                  return (
                    <div key={type}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">{planTypeLabels[type] || type}</span>
                        <span className="text-gray-500">{count}</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            type === 'CORE' ? 'bg-blue-500' :
                            type === 'TURKCE' ? 'bg-emerald-500' : 'bg-purple-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Henüz veri yok</p>
            )}
          </div>

          {/* Status Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-green-600" />
              Durum Dağılımı
            </h3>
            {analytics?.statusDistribution && Object.keys(analytics.statusDistribution).length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {Object.entries(analytics.statusDistribution).map(([status, count]) => (
                  <div
                    key={status}
                    className={`px-4 py-3 rounded-lg ${statusColors[status] || 'bg-gray-100 text-gray-700'}`}
                  >
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm">{statusLabels[status] || status}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Henüz veri yok</p>
            )}
          </div>
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Plans Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Son 7 Günlük Aktivite
            </h3>
            {analytics?.recentPlansTimeline && analytics.recentPlansTimeline.length > 0 ? (
              <div className="flex items-end justify-between gap-2 h-32">
                {analytics.recentPlansTimeline.map((item, idx) => {
                  const maxCount = Math.max(...analytics.recentPlansTimeline.map(i => i.count), 1);
                  const height = (item.count / maxCount) * 100;
                  const day = new Date(item.date).toLocaleDateString('tr-TR', { weekday: 'short' });
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div className="w-full flex flex-col items-center">
                        <span className="text-xs text-gray-600 mb-1">{item.count}</span>
                        <div
                          className="w-full bg-emerald-500 rounded-t transition-all"
                          style={{ height: `${Math.max(height, 8)}px` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 mt-2">{day}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Son 7 günde plan oluşturulmadı</p>
            )}
          </div>

          {/* Generation Success Rate */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Layers className="w-5 h-5 text-purple-600" />
              AI Üretim Durumu
            </h3>
            {analytics?.generationStatusDistribution && Object.keys(analytics.generationStatusDistribution).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(analytics.generationStatusDistribution).map(([status, count]) => {
                  const total = Object.values(analytics.generationStatusDistribution).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                  const isSuccess = status === 'completed';
                  const isFailed = status === 'failed';
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        isSuccess ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-gray-400'
                      }`} />
                      <span className="flex-1 text-sm text-gray-700 capitalize">
                        {status === 'completed' ? 'Başarılı' :
                         status === 'failed' ? 'Başarısız' :
                         status === 'generating' ? 'Üretiliyor' : status}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                      <span className="text-xs text-gray-500">({percentage}%)</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Henüz veri yok</p>
            )}
          </div>
        </div>

        {/* Tags and Development Areas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Tags */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Tag className="w-5 h-5 text-indigo-600" />
              En Popüler Etiketler
            </h3>
            {analytics?.topTags && analytics.topTags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {analytics.topTags.map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-sm"
                  >
                    <span>#{item.tag}</span>
                    <span className="px-1.5 py-0.5 bg-indigo-100 rounded text-xs">{item.count}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Henüz etiket yok</p>
            )}
          </div>

          {/* Top Development Areas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-rose-600" />
              Gelişim Alanları
            </h3>
            {analytics?.topDevelopmentAreas && analytics.topDevelopmentAreas.length > 0 ? (
              <div className="space-y-2">
                {analytics.topDevelopmentAreas.map((item, idx) => {
                  const maxVal = Math.max(...analytics.topDevelopmentAreas.map(a => a.count), 1);
                  const percentage = (item.count / maxVal) * 100;
                  return (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-700">{item.area}</span>
                        <span className="text-gray-500">{item.count} etkinlik</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-rose-500 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Henüz veri yok</p>
            )}
          </div>
        </div>

        {/* Week Map Coverage */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-teal-600" />
            Hafta Haritası Kapsamı
          </h3>
          {analytics?.weekMapCoverage && analytics.weekMapCoverage.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {analytics.weekMapCoverage.map((item, idx) => (
                <div key={idx} className="p-4 bg-teal-50 rounded-lg">
                  <p className="text-lg font-bold text-teal-700">{item.year}. Yıl</p>
                  <p className="text-sm text-teal-600">{item.weekCount} / 36 hafta tanımlı</p>
                  <div className="mt-2 h-2 bg-teal-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-500 rounded-full"
                      style={{ width: `${(item.weekCount / 36) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Henüz hafta haritası tanımlanmamış</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanStudioAnalytics;
